import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { OptionsListContainer } from '../Styles';
import { DraggableOption } from './DraggableOption';
import { Option, OptionsListProps } from '../types';

export const OptionsList: React.FC<OptionsListProps> = ({
  selectedOptions,
  handleEditOption,
  handleOptionRemove,
  soldOutLoading,
  onOrderChange,
}) => {
  const [optionOrder, setOptionOrder] = useState<(number | string)[]>([]);

  useEffect(() => {
    const sorted = [...selectedOptions]
      .filter((o) => o?.id)
      .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    setOptionOrder(sorted.map((o) => o.id));
  }, [selectedOptions]);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const prevOrderIds = Array.from(optionOrder);
    const newOrderIds = Array.from(optionOrder);
    const [moved] = newOrderIds.splice(source.index, 1);
    newOrderIds.splice(destination.index, 0, moved);
    setOptionOrder(newOrderIds);

    if (!onOrderChange) return;

    const newOrderOptions = newOrderIds
      .map((id) => selectedOptions.find((o) => o.id === id))
      .filter(Boolean) as Option[];

    onOrderChange(newOrderOptions).catch(() => {
      setOptionOrder(prevOrderIds);
    });
  }, [optionOrder, selectedOptions, onOrderChange]);

  const orderedOptions = useMemo(() => {
    return optionOrder
      .map((id) => selectedOptions.find((o) => o.id === id))
      .filter(Boolean) as Option[];
  }, [optionOrder, selectedOptions]);

  return (
    <OptionsListContainer>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="options-list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {orderedOptions.map((option, index) => (
                <Draggable
                  key={String(option.id)}
                  draggableId={String(option.id)}
                  index={index}
                >
                  {(draggableProvided) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...draggableProvided.dragHandleProps}
                    >
                      <DraggableOption
                        option={option}
                        handleEditOption={handleEditOption}
                        handleOptionRemove={handleOptionRemove}
                        soldOutLoading={soldOutLoading}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </OptionsListContainer>
  );
};
